import React, { useEffect, useState } from "react";
import { TeaWordmark } from "@/components/TeaWordmark";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RESIDENT_TOKEN_KEY } from "@/pages/PortalLoginPage";
import axios from "axios";
import { Loader2, CheckCircle2, LogOut, PlayCircle, Home, Wallet, CalendarDays, CalendarPlus, MessageSquare, Target, Menu, PenLine } from "lucide-react";
import { PortalScreen, Tile } from "@/components/portal/lp/PortalScreen";
import LessonGate from "@/components/portal/lp/LessonGate";
import MoneyTab from "@/components/portal/lp/MoneyTab";
import CalendarTab from "@/components/portal/lp/CalendarTab";
import ProgramTab from "@/components/portal/lp/ProgramTab";
import { LifePassCard, ProfileCard, RoomReadyCard } from "@/components/portal/lp/HomeCards";

function DocsWaitingBanner({ token, onOpen }) {
  const [n, setN] = useState(0);
  useEffect(() => { residentDocsApi(token).get("/resident").then((d) => setN(d.waiting || 0)).catch(() => {}); }, [token]);
  if (!n) return null;
  return (
    <button type="button" onClick={onOpen} data-testid="home-docs-banner" className="w-full flex items-center gap-3 rounded-xl border border-[#d4a017]/60 bg-[#d4a017]/10 p-4 text-left">
      <PenLine className="h-6 w-6 text-[#d4a017]" /><span><span className="block font-medium">You have {n} document{n === 1 ? "" : "s"} to sign</span><span className="text-xs text-white/60">Tap to fill in the blanks and adopt your signature</span></span>
    </button>
  );
}

const TABS = [["home", "Home", Home], ["money", "Money", Wallet], ["calendar", "Calendar", CalendarDays], ["program", "Program", Target], ["more", "More", Menu]];
import { PillarProgress } from "@/components/nds/windows/pillar/PillarProgress";
import { TeaLogo } from "@/components/TeaLogo";
import ResidentMySchedule from "@/components/nds/windows/sch/ResidentMySchedule";
import { residentDocsApi } from "@/lib/docsApi";
import { SignList } from "@/components/docs/SignList";
import { BaselineGate } from "@/components/portal/baseline/BaselineGate";
import PortalMemos from "@/components/portal/PortalMemos";
import MemoGate from "@/components/portal/MemoGate";
import AddToHomeScreen, { useResidentHomeScreen } from "@/components/portal/AddToHomeScreen";
import { WelcomeTour } from "@/components/portal/welcome/WelcomeTour";
import WeeklyQuestions from "@/components/portal/WeeklyQuestions";
import RoadmapTasks, { RoadmapBanner, useRoadmapTasks } from "@/components/portal/lp/RoadmapTasks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/re`;
const errOf = (e) => e?.response?.data?.detail || "Something went wrong. Please try again.";

function TaskCard({ a, pillar, token, residentId, onDone }) {
  const [option, setOption] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submitted = a.status === "resident_submitted";
  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await axios.post(`${API}/residents/${residentId}/pillar-assignments/${a.id}/submit`, { option_id: option, reason }, { params: { token } });
      onDone();
    } catch (e) { setError(typeof errOf(e) === "string" ? errOf(e) : "Please check your answer."); } finally { setBusy(false); }
  };
  if (a.locked) return <LessonGate a={a} pillar={pillar} token={token} onDone={onDone} />;
  return (
    <article data-testid={`portal-task-${a.id}`} className="rounded-xl border border-white/10 bg-white/[0.03] p-5 sm:p-6">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#d4a017]">
        <span className="h-2 w-2 rounded-full" style={{ background: pillar?.color_hex === "#0A0612" ? "#9b8cff" : pillar?.color_hex }} />
        {pillar?.name} · Week {a.program_week}
      </div>
      {a.node_snapshot?.educational_prompt && <p className="mt-3 text-[15px] leading-relaxed text-white/80">{a.node_snapshot.educational_prompt}</p>}
      <h2 className="mt-4 text-lg font-semibold text-white">{a.node_snapshot?.question_text}</h2>
      {submitted ? (
        <div data-testid={`portal-task-submitted-${a.id}`} className="mt-4 flex items-start gap-2 text-emerald-300 text-sm">
          <CheckCircle2 className="h-4 w-4 mt-0.5" />
          <div>Submitted — your case manager will review it.
            {a.homework_task?.title && <div className="mt-2 text-white/80">Your homework: <b>{a.homework_task.title}</b>
              <ol className="list-decimal ml-5 mt-1 space-y-0.5">{(a.homework_task.steps || []).map((s, i) => <li key={i}>{s}</li>)}</ol></div>}
          </div>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {a.node_snapshot.options.map((o) => (
            <label key={o.id} data-testid={`portal-option-${a.id}-${o.id}`}
              className={`flex gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${option === o.id ? "border-[#d4a017] bg-[#d4a017]/10" : "border-white/10 hover:border-white/30"}`}>
              <input type="radio" name={`opt-${a.id}`} value={o.id} checked={option === o.id} onChange={() => setOption(o.id)} className="mt-1 accent-[#d4a017]" />
              <span className="text-sm text-white/90"><b className="text-[#d4a017]">{o.letter}.</b> {o.text}</span>
            </label>
          ))}
          <textarea data-testid={`portal-reason-${a.id}`} rows={4} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Tell us why you chose this answer (at least 100 characters)…"
            className="w-full p-3 rounded-lg bg-black/40 border border-white/10 focus:border-[#d4a017] outline-none text-white text-sm" />
          <div className="flex items-center gap-3">
            <span className={`text-xs ${reason.trim().length >= 100 ? "text-emerald-300" : "text-white/50"}`}>{reason.trim().length}/100</span>
            {error && <span data-testid={`portal-error-${a.id}`} className="text-xs text-red-300">{error}</span>}
            <button type="button" data-testid={`portal-submit-${a.id}`} onClick={submit} disabled={!option || reason.trim().length < 100 || busy}
              className="ml-auto inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#d4a017] text-black font-semibold text-sm disabled:opacity-40">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Submit
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

export default function ResidentPortalPage() {
  const [params, setParams] = useSearchParams();
  const [screen, setScreen] = useState(null);
  const nav = useNavigate();
  useResidentHomeScreen();
  const linkToken = params.get("token");
  const token = linkToken || localStorage.getItem(RESIDENT_TOKEN_KEY);
  const signedIn = !linkToken && !!token;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [gate, setGate] = useState(null);
  const [seen, setSeen] = useState(null);
  const [replay, setReplay] = useState(false);
  const [rmTasks, setRmTasks] = useRoadmapTasks(token);
  const load = () => axios.get(`${API}/portal`, { params: { token } }).then((r) => {
    setData(r.data);
    axios.get(`${API}/portal/resident/baseline`, { params: { token } }).then((b) => setGate(b.data.required)).catch(() => setGate(false));
    axios.get(`${API}/portal/resident/welcome`, { params: { token } }).then((w) => setSeen(w.data.seen)).catch(() => setSeen(true));
  }).catch((e) => {
    if (signedIn && e?.response?.status === 401) { localStorage.removeItem(RESIDENT_TOKEN_KEY); nav("/login?as=resident", { replace: true }); return; }
    setError(errOf(e));
  });
  useEffect(() => {
    if (!token) { nav("/login?as=resident", { replace: true }); return; }
    load();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const signOut = async () => {
    await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/lp/auth/logout-pin`, null, { params: { token } }).catch(() => {});
    localStorage.removeItem(RESIDENT_TOKEN_KEY);
    nav("/login?as=resident", { replace: true });
  };

  const welcomed = () => axios.post(`${API}/portal/resident/welcome/seen`, null, { params: { token } }).finally(() => setSeen(true));
  const open = gate === false && seen === true;

  const tab = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "home";
  const go = (k, sub) => { const p = new URLSearchParams(params); p.set("tab", k); sub ? p.set("screen", sub) : p.delete("screen"); setParams(p, { replace: true }); window.scrollTo({ top: 0 }); };
  const tabBtn = (k, label, Icon, cls, id = `portal-tab-${k}`) => (
    <button key={k} type="button" onClick={() => go(k)} data-testid={id} aria-current={tab === k ? "page" : undefined}
      className={`${cls} ${tab === k ? "text-[#d4a017]" : "text-white/50 hover:text-white"}`}><Icon className="h-5 w-5" /><span>{label}</span></button>
  );

  return (
    <main data-testid="resident-portal" className="min-h-screen bg-[#0b0814] text-white px-4 pt-8 pb-28 sm:py-12">
      <MemoGate token={token} />
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-start justify-between gap-3">
          <TeaLogo size={48} testid="resident-portal-logo" />
          {signedIn && <button type="button" onClick={signOut} data-testid="resident-portal-signout" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-white/50 hover:text-white"><LogOut className="h-3.5 w-3.5" /> Sign out</button>}
        </div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#d4a017]"><TeaWordmark small text="The Exchange Alliance" testid="resident-portal-wordmark" /> · Streets to Stability</div>
        {data && open && (
          <nav data-testid="portal-tabs-top" className="hidden sm:flex gap-1 border-b border-white/10">
            {TABS.map(([k, label, Icon]) => tabBtn(k, label, Icon, `inline-flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px ${tab === k ? "border-[#d4a017]" : "border-transparent"} [&>svg]:h-4 [&>svg]:w-4`, `portal-toptab-${k}`))}
          </nav>
        )}
        {error ? <p data-testid="portal-error" className="text-red-300">{String(error)}</p>
          : !data || gate === null || seen === null ? <div className="flex items-center gap-2 text-white/60"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          : !seen ? <WelcomeTour token={token} onDone={welcomed} />
          : gate ? <BaselineGate token={token} onDone={() => setGate(false)} /> : (
          <>
            {tab === "home" && (
              <div className="space-y-6" data-testid="tab-home">
                <h1 data-testid="portal-greeting" className="text-4xl sm:text-5xl font-semibold">Hi {data.resident.first_name}.</h1>
                <p className="text-white/60 text-sm">Phase {data.resident.current_phase} · Day {data.participant_state.program_day_current} · {data.participant_state.completion_pct}% of the program complete</p>
                <AddToHomeScreen />
                <DocsWaitingBanner token={token} onOpen={() => go("more")} />
                <RoadmapBanner tasks={rmTasks} onOpen={() => go("program", "homework")} />
                <WeeklyQuestions token={token} />
                <LifePassCard token={token} />
                <RoomReadyCard token={token} />
                <div className="grid grid-cols-2 gap-2">
                  <Tile icon={CalendarPlus} label="Book a session" sub="Open classes & sessions" accent onClick={() => setScreen("sessions")} testid="home-tile-sessions" />
                  <Tile icon={MessageSquare} label="Memos" sub="Message your staff" onClick={() => setScreen("memos")} testid="home-tile-memos" />
                </div>
                {screen === "sessions" && <PortalScreen title="Book a session" onBack={() => setScreen(null)} testid="home-screen-sessions"><ResidentMySchedule token={token} sessionsOnly /></PortalScreen>}
                {screen === "memos" && <PortalScreen title="Memos" onBack={() => setScreen(null)} testid="home-screen-memos"><PortalMemos token={token} /></PortalScreen>}
              </div>
            )}
            {tab === "money" && <MoneyTab token={token} />}
            {tab === "calendar" && <CalendarTab token={token} rid={data.resident.id} />}
            {tab === "program" && (
              <ProgramTab token={token} startScreen={params.get("screen")} taskCount={data.assignments.length} lessonCount={data.assignments.filter((a) => a.locked).length} roadmapCount={(rmTasks || []).length}
                lessons={(openTasks) => (<>
                  <RoadmapTasks token={token} tasks={rmTasks} setTasks={setRmTasks} />
                  {data.assignments.filter((a) => a.locked).length ? data.assignments.filter((a) => a.locked).map((a) => (
                    <LessonGate key={a.id} a={a} pillar={data.pillars.find((p) => p.id === a.pillar_id)} token={token} onDone={load} onUnlocked={openTasks} />
                  )) : <p data-testid="portal-no-lessons" className="text-white/60">No homework lessons waiting. New ones appear with each week's pillar question.</p>}
                </>)}
                progress={<PillarProgress pillars={data.pillars} state={data.participant_state} testPrefix="portal-progress" />}
                tasks={data.assignments.length ? data.assignments.map((a) => (
                  <TaskCard key={a.id} a={a} pillar={data.pillars.find((p) => p.id === a.pillar_id)} token={token} residentId={data.resident.id} onDone={load} />
                )) : <p data-testid="portal-no-tasks" className="text-white/60">You're all caught up on pillar tasks. New ones will appear here.</p>} />
            )}
            {tab === "more" && (
              <div className="space-y-6" data-testid="tab-more">
                <ProfileCard token={token} />
                <button type="button" onClick={() => setReplay(true)} data-testid="more-replay-welcome" className="w-full flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4 text-left hover:border-[#d4a017]/60 transition-colors">
                  <PlayCircle className="h-6 w-6 text-[#d4a017]" /><span><span className="block font-medium">Replay welcome tour</span><span className="text-xs text-white/50">Your narrated walk-through of the Baseline and your portal</span></span></button>
                {replay && <WelcomeTour token={token} replay onDone={() => setReplay(false)} onClose={() => setReplay(false)} />}
                <section data-testid="portal-documents" className="space-y-3">
                  <h2 className="text-xs uppercase tracking-[0.3em] text-[#d4a017]">Your documents</h2>
                  <SignList api={residentDocsApi(token)} base="/resident" prefix="portal-docs" />
                </section>
                {signedIn && <button type="button" onClick={signOut} data-testid="more-signout" className="inline-flex items-center gap-2 rounded-md border border-white/15 px-4 py-2.5 text-sm text-white/80"><LogOut className="h-4 w-4" /> Sign out</button>}
              </div>
            )}
          </>
        )}
      </div>
      {data && open && (
        <nav data-testid="portal-tabs-bottom" className="sm:hidden fixed bottom-0 inset-x-0 z-40 grid grid-cols-5 border-t border-white/10 bg-[#0b0814]/95 backdrop-blur pb-[env(safe-area-inset-bottom)]">
          {TABS.map(([k, label, Icon]) => tabBtn(k, label, Icon, "flex flex-col items-center gap-0.5 py-2 text-[10px]"))}
        </nav>
      )}
    </main>
  );
}
